import React, { useState, useMemo } from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useTranslation } from 'react-i18next';
import TablePagination from '@mui/material/TablePagination';
import routes from "../../../utils/routes.json";
import { useNavigate } from "react-router-dom";
import './HistoriqueTable.scss';

const HistoriqueTable = ({ data, title }) => {
    const { t } = useTranslation();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const navigate = useNavigate();

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const [searchQuery, setSearchQuery] = useState('');

    const filteredData = useMemo(() => {
        if (!data) return [];
        return [...data].reverse().filter(row => {
            const searchLower = searchQuery.toLowerCase();
            return (
                row.ticker.toLowerCase().includes(searchLower) ||
                row.type.toLowerCase().includes(searchLower)
            );
        });
    }, [data, searchQuery]);

    const handleClickTicker = (ticker) => {
        navigate(`${routes.stock_nav}/${ticker}`)
    }

    return (
        <div className="historique-table">
            {data &&
                <Paper sx={{ width: '100%', overflow: 'hidden', boxShadow: 'none', background: 'transparent', border: 'none' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0px', flexWrap: 'wrap', gap: '16px' }}>
                        {title && <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#0f172a' }}>{title}</h3>}
                        <div style={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                            <input
                                type="text"
                                placeholder={t('wallet.table.search')}
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    setPage(0);
                                }}
                                style={{
                                    padding: '10px 16px',
                                    borderRadius: '12px',
                                    border: '1px solid #cbd5e1',
                                    outline: 'none',
                                    fontSize: '0.9rem',
                                    width: '100%',
                                    maxWidth: '350px',
                                    background: '#f8fafc',
                                    color: '#334155',
                                    transition: 'all 0.2s ease',
                                    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#6366f1';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
                                    e.target.style.background = '#fff';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#cbd5e1';
                                    e.target.style.boxShadow = 'inset 0 1px 2px rgba(0,0,0,0.05)';
                                    e.target.style.background = '#f8fafc';
                                }}
                            />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <TablePagination
                                sx={{ borderBottom: 'none' }}
                                rowsPerPageOptions={[10, 20, 50]}
                                component="div"
                                count={filteredData.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                labelRowsPerPage=""
                                sx={{ borderBottom: 'none', borderTop: 'none' }}
                            />
                        </div>
                    </div>
                    <TableContainer sx={{ borderRadius: '0' }}>
                        <Table sx={{ minWidth: 650 }} aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell className={"tableHeader"}>{t('wallet.table.ticker')}</TableCell>
                                    <TableCell className={"tableHeader"}>{t('wallet.table.quantity')}</TableCell>
                                    <TableCell className={"tableHeader"}>{t('wallet.table.price')}</TableCell>
                                    <TableCell className={"tableHeader"}>{t('wallet.table.date')}</TableCell>
                                    <TableCell className={"tableHeader"}>{t('wallet.table.type')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                                    <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                        <TableCell data-label={t('wallet.table.ticker')} onClick={() => handleClickTicker(row["ticker"])} className="ticker tableCell"
                                            component="th" scope="row">
                                            <div className="history-logo-container">
                                                <img
                                                    src={`https://financialmodelingprep.com/image-stock/${row.ticker}.png`}
                                                    alt={row.ticker}
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                            </div>
                                            <span className="ticker-text">{row.ticker}</span>
                                        </TableCell>
                                        <TableCell data-label={t('wallet.table.quantity')} className="tableCell quantity">{row.quantity}</TableCell>
                                        <TableCell data-label={t('wallet.table.price')} className="tableCell price">{row.price.toFixed(2)}</TableCell>
                                        <TableCell data-label={t('wallet.table.date')} className="tableCell date">{new Date(row.time).toLocaleDateString()}</TableCell>
                                        <TableCell data-label={t('wallet.table.type')} className="tableCell type">
                                            <span className={`type-pill ${row.type.toLowerCase() === 'buy' ? 'type-buy' : 'type-sell'}`}>
                                                {row.type}
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filteredData.length === 0 && (
                                    <TableRow>
                                        <TableCell className="alone-cell" colSpan={5} style={{ padding: '60px 0', borderBottom: 'none' }}>
                                            <div className="empty-state">
                                                <span className="empty-icon" style={{ opacity: 0.2 }}>🧾</span>
                                                <p style={{ color: '#64748b' }}>Vous n'avez pas d'historique (ou aucun résultat)</p>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        sx={{ borderBottom: 'none' }}
                        rowsPerPageOptions={[10, 20, 50]}
                        component="div"
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage=""
                        sx={{ borderTop: 'none' }}
                    />
                </Paper>
            }

        </div>
    );
};

export default HistoriqueTable;
